import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule, ButtonOptions } from '@usitsdasdesign/dds-ng/button';
import { InputModule, DescriptionType, TextAlign, InputOptions } from '@usitsdasdesign/dds-ng/input';
import {
  LabelPosition,
  Size,
  ErrorState,
  Themes,
  ExtThemes,
  ButtonKind,
  WidthState,
} from '@usitsdasdesign/dds-ng/shared';
import { UploaderModule, UploaderOptions, UploadFile } from '@usitsdasdesign/dds-ng/uploader';
import { SpecificationsService } from '../../core/services/specifications.service';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Specification } from '../../core/models/specification.models';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-update-specification',
  standalone: true,
  imports: [ButtonModule, InputModule, ReactiveFormsModule, UploaderModule],
  templateUrl: './update-specification.component.html',
  styleUrl: './update-specification.component.scss',
})
export class UpdateSpecificationComponent implements OnDestroy {
  projectId: string;
  specificationId: string;
  currentSpecification: Specification | null = null;
  uploadContent: string;
  isLoading: boolean = true;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private specificationsService: SpecificationsService,
  ) {}

  inputFormCtrl = new FormControl({ value: '', disabled: false });

  inputOptions: InputOptions = {
    label: 'API specification name',
    labelPosition: LabelPosition.external,
    placeholder: '',
    isError: false,
    isRequired: false,
    isInverse: false,
    size: Size.md,
    description: '',
    descriptionType: DescriptionType.text,
    errorMessage: '',
    errorState: ErrorState.default,
    textAlign: TextAlign.left,
    type: 'text',
  };

  changeValue() {
    // Clear error state when user types
    if (this.inputOptions.isError) {
      this.inputOptions = {
        ...this.inputOptions,
        isError: false,
        errorMessage: '',
      };
    }
  }

  onBlur() {}

  uploadAreaOptions: UploaderOptions = {
    theme: Themes.dark,
    heading: '',
    description: 'Upload an OpenAPI .xml, .yaml, or .yml file',
    isDragAndDrop: true,
    isMultiple: false,
    maxSize: 2000,
    maxTotalSize: 5000,
    maxFiles: 1,
    maxSymbolsName: 200,
    maxSymbolsNameError: 200,
    acceptFormats: 'application/xml, text/xml, application/x-yaml, text/yaml, text/x-yaml',
    acceptExtensions: '.xml, .yaml, .yml',
    restrictedFormats: '',
    restrictedExtensions: '',
    exceptSymbols: '_',
    btnLabel: 'Choose file',
    chooseFileBtnKind: 'primaryLoud',
    isError: false,
    errorMessage: '',
    itemErrorMessage: '',
    itemSuccessMessage: '',
    unknownExtensionsIcon: 'dds-icon_clip',
    customClass: '',
    isDownloadable: false,
    allowFilesWithoutType: true,
    disabled: false,
    isRequired: false,
    isInverse: false,
  };

  fileList: UploadFile[] = [];

  updateSpecificationButtonOptions: ButtonOptions = {
    theme: ExtThemes.green,
    kind: ButtonKind.primaryLoud,
    size: Size.md,
    width: WidthState.fixed,
    isLoading: false,
    icon: 'dds-icon_upload',
    isIconLeft: false,
    isColorBg: false,
    isInverse: false,
    disabled: false,
    ariaLabel: 'Update API Specification',
    customClass: '',
    role: 'button',
  };

  removedItem(item: UploadFile) {
    this.fileList = this.fileList.filter((file) => file.fileName !== item.fileName);
    this.uploadContent = '';

    // Clear any upload errors
    if (this.uploadAreaOptions.isError) {
      this.uploadAreaOptions = {
        ...this.uploadAreaOptions,
        isError: false,
        errorMessage: '',
      };
    }
  }

  uploadedItems(items: UploadFile[]) {
    if (!items || items.length === 0) {
      console.error('No files uploaded');
      return;
    }

    const uploadedFile = items[0];

    if (!uploadedFile.file) {
      console.error('Invalid file object');
      return;
    }

    // Clear any previous errors
    if (this.uploadAreaOptions.isError) {
      this.uploadAreaOptions = {
        ...this.uploadAreaOptions,
        isError: false,
        errorMessage: '',
      };
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      this.uploadContent = reader.result?.toString() ?? '';
      console.log('File loaded successfully:', uploadedFile.fileName);
    };

    reader.onerror = (error) => {
      console.error('Error reading file:', error);
      this.uploadAreaOptions = {
        ...this.uploadAreaOptions,
        isError: true,
        errorMessage: 'Failed to read file. Please try again.',
      };
    };

    //only 1 file allowed
    reader.readAsText(uploadedFile.file);
  }

  updateSpecificationClick(): void {
    // Validate that we have the required data
    if (!this.inputFormCtrl.value || this.inputFormCtrl.value.trim() === '') {
      console.error('Specification name is required');
      this.inputOptions = {
        ...this.inputOptions,
        isError: true,
        errorMessage: 'Please enter a specification name',
      };
      return;
    }

    if (!this.uploadContent || this.uploadContent.trim() === '') {
      console.error('Specification content is required');
      this.uploadAreaOptions = {
        ...this.uploadAreaOptions,
        isError: true,
        errorMessage: 'Please upload a specification file',
      };
      return;
    }

    this.updateSpecificationButtonOptions.isLoading = true;

    this.specificationsService
      .updateSpecification(this.projectId, this.specificationId, {
        name: this.inputFormCtrl.value ?? '',
        description: this.currentSpecification?.description ?? 'API Specification',
        content: this.uploadContent,
        version: this.currentSpecification?.version ?? '1.0.0',
      })
      .subscribe({
        next: (e) => {
          console.log('Specification updated successfully:', e);
          this.updateSpecificationButtonOptions.isLoading = false;
          this.router.navigate(['../../api-specifications'], { relativeTo: this.route });
        },
        error: (err) => {
          console.error('Error updating specification:', err);
          this.updateSpecificationButtonOptions.isLoading = false;
          this.uploadAreaOptions = {
            ...this.uploadAreaOptions,
            isError: true,
            errorMessage: 'Failed to update specification. Please try again.',
          };
        },
      });
  }

  cancelButtonOptions: ButtonOptions = {
    theme: ExtThemes.green,
    kind: ButtonKind.primary,
    size: Size.md,
    width: WidthState.fixed,
    isLoading: false,
    icon: '',
    isIconLeft: true,
    isColorBg: false,
    isInverse: false,
    disabled: false,
    ariaLabel: 'Cancel',
    customClass: '',
    role: 'button',
  };

  cancelClick(event: Event): void {
    this.router.navigate(['../../api-specifications'], { relativeTo: this.route });
  }

  ngOnInit(): void {
    // Get project ID from parent route
    const parentSub = this.route.parent?.paramMap.subscribe((params) => {
      this.projectId = params.get('id') ?? '';
    });
    if (parentSub) {
      this.subscriptions.add(parentSub);
    }

    // Get specification ID from current route
    const routeSub = this.route.paramMap.subscribe((params) => {
      this.specificationId = params.get('specId') ?? '';

      // Fetch all specifications for the project to find the current one
      if (this.projectId && this.specificationId) {
        const specSub = this.specificationsService.getSpecificationsForProject(this.projectId).subscribe({
          next: (specifications) => {
            this.currentSpecification = specifications.find((s) => s.id === this.specificationId) || null;

            if (this.currentSpecification) {
              // Prefill the form with current specification name
              this.inputFormCtrl.setValue(this.currentSpecification.name);
              // Set the current content (user will upload new version)
              this.uploadContent = this.currentSpecification.content ?? '';
              this.isLoading = false;
            } else {
              console.error('Specification not found');
              this.isLoading = false;
              // Navigate back if specification not found
              this.router.navigate(['../../api-specifications'], { relativeTo: this.route });
            }
          },
          error: (err) => {
            console.error('Error fetching specification:', err);
            this.isLoading = false;
            this.router.navigate(['../../api-specifications'], { relativeTo: this.route });
          },
        });
        this.subscriptions.add(specSub);
      }
    });
    this.subscriptions.add(routeSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
